import React, { useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import WalterLoader from '../shared/WalterLoader';
import { EQUIPMENT_TYPE_LABELS as TYPE_LABELS, RATING_LETTER_COLORS as letterRateColors } from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const ConsumptionBreakdownDetail = ({
  rows,
  loading,
  error,
  selectedSegment,
  selectedFilter,
  segmentColor,
  onClose,
}) => {
  const [viewMode, setViewMode] = useState('points');

  // Chaque dimension du "Répartir par" est résolue point d'eau par point d'eau en amont
  // par ClientBuildingDashboard.js (resolveRowTaxonomyLabels / resolveRowFunctionalUnitLabel)
  // sur enrichedWaterPointRows — il suffit de filtrer sur le champ correspondant.
  const FILTER_FIELD_BY_SELECTED_FILTER = {
    usage: 'usage_category_label',
    subcategory: 'usage_subcategory_label',
    functional_unit: 'functional_unit_label',
  };
  const filterField = FILTER_FIELD_BY_SELECTED_FILTER[selectedFilter];
  const canFilterBySegment = Boolean(filterField);

  const visibleRows = useMemo(() => {
    if (!rows) return [];
    if (canFilterBySegment) {
      return rows.filter((row) => row[filterField] === selectedSegment);
    }
    return rows;
  }, [rows, selectedSegment, canFilterBySegment, filterField]);

  const totalVolume = useMemo(
    () => (rows || []).reduce((sum, row) => sum + (row.annual_volume || 0), 0),
    [rows]
  );
  const segmentVolume = useMemo(
    () => visibleRows.reduce((sum, row) => sum + (row.annual_volume || 0), 0),
    [visibleRows]
  );
  const segmentPercentage = totalVolume > 0 ? (segmentVolume / totalVolume) * 100 : 0;

  const sortedPoints = useMemo(
    () => [...visibleRows].sort((a, b) => (b.annual_volume || 0) - (a.annual_volume || 0)),
    [visibleRows]
  );

  const maxPointVolume = sortedPoints.length ? (sortedPoints[0].annual_volume || 0) : 0;

  const sortedSections = useMemo(() => {
    const bySection = {};
    visibleRows.forEach((row) => {
      const key = row.name_section || 'Non renseigné';
      bySection[key] = (bySection[key] || 0) + (row.annual_volume || 0);
    });
    const entries = Object.entries(bySection).sort((a, b) => b[1] - a[1]);
    const maxValue = entries.length ? entries[0][1] : 0;
    return { entries, maxValue };
  }, [visibleRows]);

  return (
    <div className="cbd-callout" style={{ '--cbd-accent': segmentColor || 'var(--color-primary)' }}>
      <div className="cbd-callout-header">
        <div className="cbd-callout-title">
          <span className="cbd-callout-dot" />
          <span>{selectedSegment}</span>
        </div>
        <button className="cbd-callout-close" onClick={onClose} aria-label="Fermer le détail">
          <FaTimes />
        </button>
      </div>

      <div className="cbd-callout-summary">
        <span className="cbd-callout-summary-value">{formatValue(segmentVolume)} m³</span>
        <span className="cbd-callout-summary-percent">{segmentPercentage.toFixed(1)}% du total</span>
      </div>

      {loading ? (
        <div className="cbd-loading">
          <WalterLoader size={56} />
        </div>
      ) : error ? (
        <p className="cbd-empty">Erreur : {error}</p>
      ) : (
        <>
          <div className="cbd-view-toggle">
            <button
              className={viewMode === 'points' ? 'active' : ''}
              onClick={() => setViewMode('points')}
            >
              Points d'eau
            </button>
            <button
              className={viewMode === 'sections' ? 'active' : ''}
              onClick={() => setViewMode('sections')}
            >
              Sections
            </button>
          </div>

          <div className="cbd-callout-body">
            {visibleRows.length === 0 ? (
              <p className="cbd-empty">Aucun point d'eau enregistré pour cette sélection.</p>
            ) : viewMode === 'points' ? (
              <div className="cbd-point-list">
                {sortedPoints.map((row, index) => {
                  const pct = maxPointVolume ? ((row.annual_volume || 0) / maxPointVolume) * 100 : 0;
                  return (
                    <div className="cbd-point-row" key={`${row.equipment_id}-${index}`}>
                      <div className="cbd-point-main">
                        <span className="cbd-point-type">{TYPE_LABELS[row.type_equipment] || row.type_equipment}</span>
                        <span className="cbd-point-room">{row.name_room || '-'}</span>
                      </div>
                      <span
                        className="letter-rate-badge cbd-point-badge"
                        style={{ backgroundColor: letterRateColors[row.rating_letter] || '#ccc' }}
                      >
                        {row.rating_letter || '-'}
                      </span>
                      <div className="cbd-point-gauge-wrapper">
                        <div
                          className="cbd-point-gauge-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="cbd-point-value">{formatValue(row.annual_volume)} m³</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="cbd-section-list">
                {sortedSections.entries.map(([sectionName, volume]) => {
                  const pct = sortedSections.maxValue ? (volume / sortedSections.maxValue) * 100 : 0;
                  return (
                    <div className="consumption-gauge-row" key={sectionName}>
                      <span className="usage-name">{sectionName}</span>
                      <div className="usage-bar-wrapper">
                        <div className="usage-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="usage-value">{formatValue(volume)} m³/an</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ConsumptionBreakdownDetail;
